/**
 * Shared date/time formatting utilities.
 *
 * The backend returns timestamps in two forms:
 *   - ISO strings  e.g. "2026-04-11 08:18:52+00"  (from raw epgsql TIMESTAMPTZ columns)
 *   - Epoch seconds as integers  e.g. 1775881132   (from EXTRACT(EPOCH FROM …)::bigint)
 *
 * All formatters accept either form plus null/undefined and produce a safe fallback of "—".
 *
 * Requested output format: YY/MM/DD HH:MM  (24-hour, no AM/PM)
 */

type TsInput = string | number | null | undefined;

/** Convert any supported timestamp representation to a Date, or null on failure. */
function toDate(ts: TsInput): Date | null {
  if (ts == null) return null;

  if (typeof ts === 'number') {
    if (!Number.isFinite(ts) || ts <= 0) return null;
    // < 1e12 → treat as epoch seconds; otherwise epoch milliseconds
    const ms = ts < 1_000_000_000_000 ? ts * 1000 : ts;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }

  // String: might be a numeric string or an ISO datetime
  const n = Number(ts);
  if (Number.isFinite(n) && n > 0) {
    const ms = n < 1_000_000_000_000 ? n * 1000 : n;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}

/** YY/MM/DD HH:MM (24-hour) */
export function fmtDateTime(ts: TsInput): string {
  const d = toDate(ts);
  if (!d) return '—';
  const yy = String(d.getFullYear()).slice(2);
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${yy}/${mo}/${dd} ${hh}:${mm}`;
}

/** HH:MM (24-hour, time only) */
export function fmtTime(ts: TsInput): string {
  const d = toDate(ts);
  if (!d) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** "1h 23m" / "45m" / "In progress" */
export function fmtDuration(start: TsInput, end: TsInput): string {
  const s = toDate(start);
  const e = toDate(end);
  if (!s || !e) return 'In progress';
  const totalMin = Math.floor((e.getTime() - s.getTime()) / 60_000);
  if (totalMin < 0) return '—';
  const h = Math.floor(totalMin / 60);
  return h > 0 ? `${h}h ${totalMin % 60}m` : `${totalMin}m`;
}
